import { ReactElement, useEffect, useState } from 'react';
import {
  Datagrid,
  FilterForm,
  FunctionField,
  List,
  Pagination,
  SelectInput,
  TopToolbar,
  useNotify,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { EmptyContent } from 'src/components/empty-content';
import { PermissionButton } from 'src/routes/components/permission-button';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { ProviderItem, listProviders } from 'src/pages/admin/services/xbitRelayService';
import { RebalanceItem } from 'src/pages/admin/services/xbitRelayRebalancesService';

import { ApproveDialog } from './ApproveDialog';
import { RejectDialog } from './RejectDialog';
import { ResolveDialog } from './ResolveDialog';
import {
  REBALANCE_ROWS_PER_PAGE,
  REBALANCE_STATUSES,
  XBIT_RELAY_REBALANCES_APPROVE_ACTION,
  XBIT_RELAY_REBALANCES_PERMISSION_RESOURCE,
  XBIT_RELAY_REBALANCES_REJECT_ACTION,
  XBIT_RELAY_REBALANCES_RESOLVE_ACTION,
} from './constants';
import {
  canApproveRebalance,
  canRejectRebalance,
  canResolveRebalance,
  chainLabel,
  destinationTokenSymbol,
  explorerTxUrl,
  formatAmount,
  formatUnixMs,
  isTokenSwap,
  sourceTokenSymbol,
  statusChipColor,
  truncateMiddle,
} from './helpers';
import './init';

const RebalancesListActions = ({ providers }: { providers: ProviderItem[] }) => (
  <TopToolbar
    sx={{
      minHeight: 90,
      alignItems: 'center',
      px: 3,
      pt: 1,
      overflow: 'visible',
      width: '100%',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        flexWrap: 'wrap',
        '& form': { display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', width: '100%' },
        '& .ra-input': { mt: 0, mb: 0 },
        '& .MuiFormControl-root': { mt: 0, mb: 0 },
        '& .MuiInputBase-root': { minHeight: 40 },
      }}
    >
      <FilterForm
        filters={[
          <SelectInput
            key="provider"
            source="provider"
            label="module.xbitRelayHotWalletBalancer.provider"
            choices={providers.map((item) => ({ id: item.provider, name: item.provider }))}
            alwaysOn
            resettable
            emptyText="actions.all"
            size="small"
            sx={{ minWidth: 240 }}
          />,
          // Matched exactly by the BE, so the choices are the raw lifecycle values — not
          // translated labels that would send the wrong string.
          <SelectInput
            key="status"
            source="status"
            label="module.xbitRelayHotWalletBalancer.status"
            choices={REBALANCE_STATUSES.map((item) => ({ id: item, name: item }))}
            alwaysOn
            resettable
            emptyText="actions.all"
            size="small"
            sx={{ minWidth: 240 }}
          />,
        ]}
      />
    </Box>
  </TopToolbar>
);

/**
 * Source and destination can carry different tokens, so each side names its own: chain and token
 * on top, the hot wallet address below.
 */
const RouteCell = ({
  chainId,
  tokenSymbol,
  address,
  onCopy,
}: {
  chainId: string;
  tokenSymbol: string;
  address: string;
  onCopy(value: string): void;
}) => (
  <Stack spacing={0.25} sx={{ whiteSpace: 'nowrap' }}>
    <Stack direction="row" spacing={0.75} alignItems="baseline">
      <Typography variant="body2">{chainLabel(chainId)}</Typography>
      {tokenSymbol && (
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {tokenSymbol}
        </Typography>
      )}
    </Stack>
    <Tooltip title={address || ''} arrow>
      <Typography
        variant="caption"
        sx={{ fontFamily: 'monospace', cursor: 'pointer', color: 'text.secondary' }}
        onClick={() => onCopy(address)}
      >
        {truncateMiddle(address)}
      </Typography>
    </Tooltip>
  </Stack>
);

/** One symbol normally; `USDT → USDC` when the rebalance swaps as well as bridges. */
const TokenPairCell = ({ record }: { record: RebalanceItem }) => {
  const from = sourceTokenSymbol(record);
  const to = destinationTokenSymbol(record);

  if (!isTokenSwap(record)) {
    return (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {from || to || '-'}
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {from}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        →
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {to}
      </Typography>
    </Stack>
  );
};

type TxValueProps = {
  chainId: string;
  txHash: string;
  onCopy(value: string): void;
};

/** Links to the explorer when the chain has one, otherwise falls back to click-to-copy. */
const TxValue = ({ chainId, txHash, onCopy }: TxValueProps) => {
  if (!txHash) {
    return (
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        -
      </Typography>
    );
  }

  const url = explorerTxUrl(chainId, txHash);
  if (!url) {
    return (
      <Tooltip title={txHash} arrow>
        <Typography
          variant="caption"
          sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
          onClick={() => onCopy(txHash)}
        >
          {truncateMiddle(txHash)}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={txHash} arrow>
      <Link
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{ fontFamily: 'monospace', fontSize: 12 }}
      >
        {truncateMiddle(txHash)}
      </Link>
    </Tooltip>
  );
};

/**
 * Both legs of the transfer in one column. They fill in one after the other as the rebalance
 * progresses, so reading them stacked shows how far it has got at a glance.
 */
const TxPairCell = ({
  record,
  onCopy,
}: {
  record: RebalanceItem;
  onCopy(value: string): void;
}) => {
  const t = useTranslate();

  const legs = [
    { key: 'src', label: t('module.xbitRelayHotWalletBalancer.srcShort'), chainId: record.srcChainId, txHash: record.srcTxHash },
    { key: 'dst', label: t('module.xbitRelayHotWalletBalancer.dstShort'), chainId: record.dstChainId, txHash: record.dstTxHash },
  ];

  return (
    <Stack spacing={0.25} sx={{ whiteSpace: 'nowrap' }}>
      {legs.map((leg) => (
        <Stack key={leg.key} direction="row" spacing={0.75} alignItems="baseline">
          <Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 24 }}>
            {leg.label}
          </Typography>
          <TxValue chainId={leg.chainId} txHash={leg.txHash} onCopy={onCopy} />
        </Stack>
      ))}
    </Stack>
  );
};

const XbitRelayHotWalletBalancerList = () => {
  const t = useTranslate();
  const notify = useNotify();
  const [providers, setProviders] = useState<ProviderItem[]>([]);
  const [popup, setPopup] = useState<ReactElement>();

  useEffect(() => {
    listProviders()
      .then((res) => setProviders(res.data?.providers ?? []))
      .catch(() => setProviders([]));
  }, []);

  const closePopup = () => setPopup(undefined);

  const copy = (value: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    notify('module.xbitRelayHotWalletBalancer.copied', { type: 'info' });
  };

  return (
    <PermissionGuard
      resource={XBIT_RELAY_REBALANCES_PERMISSION_RESOURCE}
      action="read"
      fallback={<ErrorPage403 />}
    >
      <DashboardContent>
        <CustomBreadcrumbs
          heading={t('module.xbitRelayHotWalletBalancer.title')}
          links={[
            { name: t('resource.xbitRelay'), href: '#' },
            { name: t('module.xbitRelayHotWalletBalancer.title') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={<RebalancesListActions providers={providers} />}
            empty={false}
            perPage={20}
            exporter={false}
            pagination={<Pagination rowsPerPageOptions={REBALANCE_ROWS_PER_PAGE} />}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={<EmptyContent />}
                sx={{
                  '& .RaDatagrid-headerCell': { fontWeight: 600 },
                  '& .RaDatagrid-rowCell': { py: 1.25 },
                }}
              >
                <FunctionField
                  label="module.xbitRelayHotWalletBalancer.createdAt"
                  sortable={false}
                  render={(record: RebalanceItem) => (
                    <Tooltip title={formatUnixMs(record.createdAtUnixMs)} arrow>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}
                      >
                        {formatUnixMs(record.createdAtUnixMs, 'MM-DD HH:mm:ss')}
                      </Typography>
                    </Tooltip>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayHotWalletBalancer.provider"
                  sortable={false}
                  render={(record: RebalanceItem) => (
                    <Typography variant="body2">{record.provider || '-'}</Typography>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayHotWalletBalancer.status"
                  sortable={false}
                  render={(record: RebalanceItem) => (
                    <Chip
                      label={record.status || '-'}
                      size="small"
                      color={statusChipColor(record.status)}
                      variant="outlined"
                    />
                  )}
                />
                {/* <FunctionField
                  label="module.xbitRelayHotWalletBalancer.token"
                  sortable={false}
                  render={(record: RebalanceItem) => <TokenPairCell record={record} />}
                /> */}
                <FunctionField
                  label="module.xbitRelayHotWalletBalancer.amount"
                  sortable={false}
                  render={(record: RebalanceItem) => (
                    <Stack spacing={0.25} sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {formatAmount(record.amount)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                      >
                        {t('module.xbitRelayHotWalletBalancer.minShort')}{' '}
                        {formatAmount(record.minReceived)} {destinationTokenSymbol(record)}
                      </Typography>
                    </Stack>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayHotWalletBalancer.from"
                  sortable={false}
                  render={(record: RebalanceItem) => (
                    <RouteCell
                      chainId={record.srcChainId}
                      tokenSymbol={sourceTokenSymbol(record)}
                      address={record.srcAddress}
                      onCopy={copy}
                    />
                  )}
                />
                <FunctionField
                  label="module.xbitRelayHotWalletBalancer.to"
                  sortable={false}
                  render={(record: RebalanceItem) => (
                    <RouteCell
                      chainId={record.dstChainId}
                      tokenSymbol={destinationTokenSymbol(record)}
                      address={record.dstAddress}
                      onCopy={copy}
                    />
                  )}
                />
                <FunctionField
                  label="module.xbitRelayHotWalletBalancer.txHash"
                  sortable={false}
                  render={(record: RebalanceItem) => (
                    <TxPairCell record={record} onCopy={copy} />
                  )}
                />
                <FunctionField
                  label="module.xbitRelayHotWalletBalancer.attempts"
                  sortable={false}
                  render={(record: RebalanceItem) => (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {record.attempts ?? 0}
                    </Typography>
                  )}
                />
                <FunctionField
                  label="module.xbitRelayHotWalletBalancer.failureReason"
                  sortable={false}
                  render={(record: RebalanceItem) =>
                    record.failureReason ? (
                      <Tooltip title={record.failureReason} arrow>
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{
                            maxWidth: 220,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: 'help',
                          }}
                        >
                          {record.failureReason}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2">-</Typography>
                    )
                  }
                />
                <FunctionField
                  label="common.actions"
                  sortable={false}
                  render={(record: RebalanceItem) => (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {canApproveRebalance(record.status) && (
                        <PermissionButton
                          resource={XBIT_RELAY_REBALANCES_PERMISSION_RESOURCE}
                          action={XBIT_RELAY_REBALANCES_APPROVE_ACTION}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            onClick={() =>
                              setPopup(<ApproveDialog record={record} onClose={closePopup} />)
                            }
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {t('module.xbitRelayHotWalletBalancer.approve')}
                          </Button>
                        </PermissionButton>
                      )}
                      {canRejectRebalance(record.status) && (
                        <PermissionButton
                          resource={XBIT_RELAY_REBALANCES_PERMISSION_RESOURCE}
                          action={XBIT_RELAY_REBALANCES_REJECT_ACTION}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() =>
                              setPopup(<RejectDialog record={record} onClose={closePopup} />)
                            }
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {t('module.xbitRelayHotWalletBalancer.reject')}
                          </Button>
                        </PermissionButton>
                      )}
                      {canResolveRebalance(record.status) && (
                        <PermissionButton
                          resource={XBIT_RELAY_REBALANCES_PERMISSION_RESOURCE}
                          action={XBIT_RELAY_REBALANCES_RESOLVE_ACTION}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={() =>
                              setPopup(<ResolveDialog record={record} onClose={closePopup} />)
                            }
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {t('module.xbitRelayHotWalletBalancer.resolve')}
                          </Button>
                        </PermissionButton>
                      )}
                    </Stack>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <Dialog open={Boolean(popup)} onClose={closePopup} maxWidth="sm" fullWidth>
          {popup}
        </Dialog>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default XbitRelayHotWalletBalancerList;
